/**
 * DIFF-2.2 — Reconciliation wizard.
 *
 * Opened from the "Reconcile" button on ComparisonReportPage. Lets a public
 * adjuster clean up the four things that make a raw comparison noisy, by
 * writing to the comparison's persisted `decisions` layer:
 *
 *   1. Room matches   — alias an estimate-B room name to its estimate-A room.
 *   2. Swaps          — confirm or reject SWAP_CANDIDATE entries.
 *   3. Unmapped items — assign cat/sel/activity to UNMAPPED entries.
 *   4. Equivalences   — pair a REMOVED (A-only) code with an ADDED (B-only)
 *                       code that represent the same physical work.
 *
 * Each change PATCHes the full decisions object (the API overwrites, it does
 * not merge) and the server re-derives the report; the wizard shows the
 * refreshed report immediately. Modeled on the existing modal pattern in
 * RuleAuthoringModal.tsx (overlay + white card + header/body/footer, no new
 * modal system introduced).
 */
import * as React from 'react';
import { X, Check, XCircle, ArrowRightLeft } from 'lucide-react';

import { ComparisonsAPI } from '../lib/comparisonsApi';
import type {
  ComparisonDecisions,
  ComparisonDetail,
  DiffEntry,
  EquivalenceSuggestion,
} from '../lib/comparisonsApi';
import { cn, getErrorMessage } from '../lib/utils';

type Step = 'rooms' | 'swaps' | 'unmapped' | 'equivalences';

const STEPS: { id: Step; label: string }[] = [
  { id: 'rooms', label: 'Room matches' },
  { id: 'swaps', label: 'Swaps' },
  { id: 'unmapped', label: 'Unmapped items' },
  { id: 'equivalences', label: 'Equivalences' },
];

function codeLabel(item: { cat: string; sel: string; activity: string | null } | null): string {
  if (!item) return '—';
  return [item.cat, item.sel, item.activity].filter(Boolean).join(' ');
}

interface ReconciliationWizardProps {
  comparison: ComparisonDetail;
  onClose: (updated?: ComparisonDetail) => void;
}

const ReconciliationWizard: React.FC<ReconciliationWizardProps> = ({ comparison, onClose }) => {
  const [step, setStep] = React.useState<Step>('rooms');
  const [detail, setDetail] = React.useState<ComparisonDetail>(comparison);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const decisions = detail.decisions || {};
  const report = detail.report;

  const persist = React.useCallback(
    async (next: ComparisonDecisions) => {
      setSaving(true);
      setError(null);
      try {
        const updated = await ComparisonsAPI.patchDecisions(detail.id, next);
        setDetail(updated);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setSaving(false);
      }
    },
    [detail.id],
  );

  // --- Step 1: room matches ---------------------------------------------
  const { aRooms, unmatchedBRooms } = React.useMemo(() => {
    const bRooms = new Set<string>();
    const aRoomSet = new Set<string>();
    for (const entry of report.whole) {
      if (entry.comparison?.room) bRooms.add(entry.comparison.room);
      if (entry.baseline?.room) aRoomSet.add(entry.baseline.room);
    }
    const unmatched = [...bRooms].filter((r) => !aRoomSet.has(r)).sort();
    return { aRooms: [...aRoomSet].sort(), unmatchedBRooms: unmatched };
  }, [report.whole]);

  const setRoomAlias = (bRoom: string, aRoom: string) => {
    const next: Record<string, string> = { ...(decisions.room_aliases || {}) };
    if (aRoom) {
      next[bRoom] = aRoom;
    } else {
      delete next[bRoom];
    }
    void persist({ ...decisions, room_aliases: next });
  };

  // --- Step 2: swaps -------------------------------------------------------
  const swapEntries = React.useMemo(
    () => report.different.filter((e) => e.kind === 'SWAP_CANDIDATE'),
    [report.different],
  );

  const setSwapDecision = (entry: DiffEntry, action: 'confirm' | 'reject' | 'undecided') => {
    const confirms = new Set(decisions.swap_confirms || []);
    const rejects = new Set(decisions.swap_rejects || []);
    confirms.delete(entry.key);
    rejects.delete(entry.key);
    if (action === 'confirm') confirms.add(entry.key);
    if (action === 'reject') rejects.add(entry.key);
    void persist({
      ...decisions,
      swap_confirms: [...confirms],
      swap_rejects: [...rejects],
    });
  };

  const swapStateFor = (key: string): 'confirm' | 'reject' | 'undecided' => {
    if ((decisions.swap_confirms || []).includes(key)) return 'confirm';
    if ((decisions.swap_rejects || []).includes(key)) return 'reject';
    return 'undecided';
  };

  // --- Step 3: unmapped items ---------------------------------------------
  const unmappedEntries = React.useMemo(
    () => report.different.filter((e) => e.kind === 'UNMAPPED'),
    [report.different],
  );

  const [unmappedDrafts, setUnmappedDrafts] = React.useState<
    Record<string, { cat: string; sel: string; activity: string }>
  >({});

  const draftFor = (sourceRef: string) =>
    unmappedDrafts[sourceRef] ??
    decisions.unmapped_codes?.[sourceRef] ?? { cat: '', sel: '', activity: '' };

  const updateDraft = (sourceRef: string, patch: Partial<{ cat: string; sel: string; activity: string }>) => {
    setUnmappedDrafts((d) => ({ ...d, [sourceRef]: { ...draftFor(sourceRef), ...patch } }));
  };

  const saveUnmappedCode = (sourceRef: string) => {
    const draft = draftFor(sourceRef);
    const next = { ...(decisions.unmapped_codes || {}) };
    if (draft.cat.trim() || draft.sel.trim() || draft.activity.trim()) {
      next[sourceRef] = { cat: draft.cat.trim(), sel: draft.sel.trim(), activity: draft.activity.trim() };
    } else {
      delete next[sourceRef];
    }
    void persist({ ...decisions, unmapped_codes: next });
  };

  const clearUnmappedCode = (sourceRef: string) => {
    const next = { ...(decisions.unmapped_codes || {}) };
    delete next[sourceRef];
    setUnmappedDrafts((d) => ({ ...d, [sourceRef]: { cat: '', sel: '', activity: '' } }));
    void persist({ ...decisions, unmapped_codes: next });
  };

  // --- Step 4: equivalences ------------------------------------------------
  const removedEntries = React.useMemo(
    () => report.different.filter((e) => e.kind === 'REMOVED'),
    [report.different],
  );
  const addedEntries = React.useMemo(
    () => report.different.filter((e) => e.kind === 'ADDED'),
    [report.different],
  );

  const [selectedRemoved, setSelectedRemoved] = React.useState<string | null>(null);
  const [selectedAdded, setSelectedAdded] = React.useState<string | null>(null);

  const markEquivalent = () => {
    const removed = removedEntries.find((e) => e.key === selectedRemoved);
    const added = addedEntries.find((e) => e.key === selectedAdded);
    if (!removed?.baseline || !added?.comparison) return;
    const pair = {
      from: { cat: removed.baseline.cat, sel: removed.baseline.sel },
      to: { cat: added.comparison.cat, sel: added.comparison.sel },
    };
    const next = [...(decisions.equivalences || []), pair];
    setSelectedRemoved(null);
    setSelectedAdded(null);
    void persist({ ...decisions, equivalences: next });
  };

  const removeEquivalence = (index: number) => {
    const next = (decisions.equivalences || []).filter((_, i) => i !== index);
    void persist({ ...decisions, equivalences: next });
  };

  // One-click accept of a deterministic suggestion (B-656). Equivalence is a
  // code-pair decision, so this resolves every room sharing that pair at once.
  const acceptSuggestion = (fromCat: string, fromSel: string, toCat: string, toSel: string) => {
    const pair = { from: { cat: fromCat, sel: fromSel }, to: { cat: toCat, sel: toSel } };
    void persist({ ...decisions, equivalences: [...(decisions.equivalences || []), pair] });
  };

  // Hide a suggestion once its code-pair is already confirmed.
  const confirmedPairs = new Set(
    (decisions.equivalences || []).map((e) => `${e.from.cat}:${e.from.sel}>${e.to.cat}:${e.to.sel}`),
  );
  const openSuggestions = (report.suggested_equivalences || []).filter(
    (s) => !confirmedPairs.has(`${s.from_cat}:${s.from_sel}>${s.to_cat}:${s.to_sel}`),
  );

  // --- render ---------------------------------------------------------------
  const progressLabel = `${(decisions.swap_confirms?.length || 0) + (decisions.swap_rejects?.length || 0)}/${swapEntries.length} swaps · ${Object.keys(decisions.unmapped_codes || {}).length}/${unmappedEntries.length} unmapped`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => onClose(detail)}
      role="dialog"
      aria-modal="true"
      aria-label="Reconcile comparison"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold">Reconcile comparison</h2>
            <p className="text-xs text-muted-foreground">{progressLabel}</p>
          </div>
          <button
            type="button"
            onClick={() => onClose(detail)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Step tabs */}
        <div className="flex gap-1 border-b bg-muted px-4 pt-2">
          {STEPS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStep(s.id)}
              className={cn(
                'rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                step === s.id
                  ? 'bg-card text-primary border border-b-white'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="border-b border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 px-6 py-2 text-sm text-red-800 dark:text-red-300">{error}</div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 'rooms' && (
            <RoomsStep
              unmatchedBRooms={unmatchedBRooms}
              aRooms={aRooms}
              roomAliases={decisions.room_aliases || {}}
              onChange={setRoomAlias}
            />
          )}

          {step === 'swaps' && (
            <SwapsStep entries={swapEntries} stateFor={swapStateFor} onDecide={setSwapDecision} />
          )}

          {step === 'unmapped' && (
            <UnmappedStep
              entries={unmappedEntries}
              draftFor={draftFor}
              onUpdateDraft={updateDraft}
              onSave={saveUnmappedCode}
              onClear={clearUnmappedCode}
              savedCodes={decisions.unmapped_codes || {}}
            />
          )}

          {step === 'equivalences' && (
            <EquivalencesStep
              suggestions={openSuggestions}
              onAcceptSuggestion={acceptSuggestion}
              removedEntries={removedEntries}
              addedEntries={addedEntries}
              selectedRemoved={selectedRemoved}
              selectedAdded={selectedAdded}
              onSelectRemoved={setSelectedRemoved}
              onSelectAdded={setSelectedAdded}
              onMarkEquivalent={markEquivalent}
              equivalences={decisions.equivalences || []}
              onRemoveEquivalence={removeEquivalence}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t bg-muted px-6 py-3">
          <span className="text-xs text-muted-foreground">{saving ? 'Saving…' : 'Saved'}</span>
          <button
            type="button"
            onClick={() => onClose(detail)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1: Room matches
// ---------------------------------------------------------------------------

const RoomsStep: React.FC<{
  unmatchedBRooms: string[];
  aRooms: string[];
  roomAliases: Record<string, string>;
  onChange: (bRoom: string, aRoom: string) => void;
}> = ({ unmatchedBRooms, aRooms, roomAliases, onChange }) => {
  if (unmatchedBRooms.length === 0) {
    return <p className="text-sm text-muted-foreground">Every estimate-B room matches an estimate-A room name.</p>;
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        These estimate-B rooms have no matching name on estimate A. Map each one to its
        corresponding room so line items compare correctly.
      </p>
      {unmatchedBRooms.map((room) => (
        <div key={room} className="flex items-center gap-3 rounded-md border p-3">
          <span className="min-w-[10rem] text-sm font-medium">{room}</span>
          <span className="text-xs text-muted-foreground">maps to</span>
          <select
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            value={roomAliases[room] || ''}
            onChange={(e) => onChange(room, e.target.value)}
            aria-label={`Map ${room} to`}
          >
            <option value="">Leave as-is</option>
            {aRooms.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 2: Swaps
// ---------------------------------------------------------------------------

const SwapsStep: React.FC<{
  entries: DiffEntry[];
  stateFor: (key: string) => 'confirm' | 'reject' | 'undecided';
  onDecide: (entry: DiffEntry, action: 'confirm' | 'reject' | 'undecided') => void;
}> = ({ entries, stateFor, onDecide }) => {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No possible swaps to review.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const s = stateFor(entry.key);
        return (
          <div key={entry.key} className="rounded-md border p-3">
            {entry.room && (
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {entry.room}
              </div>
            )}
            <div className="mb-2 grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-xs uppercase text-muted-foreground">Estimate A</div>
                <div className="font-mono text-xs text-foreground">{codeLabel(entry.baseline)}</div>
                <div className="text-sm">{entry.baseline?.description}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-muted-foreground">Estimate B</div>
                <div className="font-mono text-xs text-foreground">{codeLabel(entry.comparison)}</div>
                <div className="text-sm">{entry.comparison?.description}</div>
              </div>
            </div>
            {entry.polarity !== 'UNKNOWN' && (
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                {entry.polarity === 'UPGRADE' ? 'Upgrade' : 'Downgrade'}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onDecide(entry, 'confirm')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium',
                  s === 'confirm' ? 'border-emerald-300 dark:border-emerald-500/30 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300' : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <Check className="h-3.5 w-3.5" /> Confirm swap
              </button>
              <button
                type="button"
                onClick={() => onDecide(entry, 'reject')}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium',
                  s === 'reject' ? 'border-rose-300 dark:border-rose-500/30 bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300' : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <XCircle className="h-3.5 w-3.5" /> Reject (split)
              </button>
              <button
                type="button"
                onClick={() => onDecide(entry, 'undecided')}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs font-medium',
                  s === 'undecided' ? 'border bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted',
                )}
              >
                Undecided
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 3: Unmapped items
// ---------------------------------------------------------------------------

const UnmappedStep: React.FC<{
  entries: DiffEntry[];
  draftFor: (sourceRef: string) => { cat: string; sel: string; activity: string };
  onUpdateDraft: (sourceRef: string, patch: Partial<{ cat: string; sel: string; activity: string }>) => void;
  onSave: (sourceRef: string) => void;
  onClear: (sourceRef: string) => void;
  savedCodes: Record<string, { cat: string; sel: string; activity: string }>;
}> = ({ entries, draftFor, onUpdateDraft, onSave, onClear, savedCodes }) => {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No unmapped items.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const item = entry.baseline ?? entry.comparison;
        const sourceRef = item?.source_ref || '';
        const draft = draftFor(sourceRef);
        const isSaved = Boolean(savedCodes[sourceRef]);
        return (
          <div key={sourceRef || entry.key} className="rounded-md border p-3">
            <div className="mb-2 text-sm">{item?.description}</div>
            <div className="grid grid-cols-3 gap-2">
              <input
                placeholder="CAT"
                value={draft.cat}
                onChange={(e) => onUpdateDraft(sourceRef, { cat: e.target.value })}
                aria-label={`Cat for ${sourceRef}`}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <input
                placeholder="SEL"
                value={draft.sel}
                onChange={(e) => onUpdateDraft(sourceRef, { sel: e.target.value })}
                aria-label={`Sel for ${sourceRef}`}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <input
                placeholder="Activity"
                value={draft.activity}
                onChange={(e) => onUpdateDraft(sourceRef, { activity: e.target.value })}
                aria-label={`Activity for ${sourceRef}`}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => onSave(sourceRef)}
                disabled={!sourceRef}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                Save code
              </button>
              {isSaved && (
                <button
                  type="button"
                  onClick={() => onClear(sourceRef)}
                  className="rounded-md px-3 py-1.5 text-xs text-foreground hover:bg-muted"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 4: Equivalences
// ---------------------------------------------------------------------------

const EquivalencesStep: React.FC<{
  suggestions: EquivalenceSuggestion[];
  onAcceptSuggestion: (fromCat: string, fromSel: string, toCat: string, toSel: string) => void;
  removedEntries: DiffEntry[];
  addedEntries: DiffEntry[];
  selectedRemoved: string | null;
  selectedAdded: string | null;
  onSelectRemoved: (key: string) => void;
  onSelectAdded: (key: string) => void;
  onMarkEquivalent: () => void;
  equivalences: { from: { cat: string; sel: string }; to: { cat: string; sel: string } }[];
  onRemoveEquivalence: (index: number) => void;
}> = ({
  suggestions,
  onAcceptSuggestion,
  removedEntries,
  addedEntries,
  selectedRemoved,
  selectedAdded,
  onSelectRemoved,
  onSelectAdded,
  onMarkEquivalent,
  equivalences,
  onRemoveEquivalence,
}) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">
      When a line removed from A is the same physical work as a line added in B under a
      different code, mark them equivalent so they count as agreed. Suggested matches are
      pre-computed from the descriptions — confirm the ones that are truly the same work.
    </p>

    {/* Deterministic suggestions — the fast path (B-656). */}
    {suggestions.length > 0 && (
      <div className="rounded-md border">
        <div className="border-b bg-teal-50 dark:bg-teal-500/15 px-3 py-1.5 text-xs font-semibold text-teal-800 dark:text-teal-300">
          Suggested matches · {suggestions.length}
        </div>
        <ul className="divide-y">
          {suggestions.map((s) => (
            <li key={`${s.from_cat}:${s.from_sel}>${s.to_cat}:${s.to_sel}`} className="flex items-start gap-3 px-3 py-2">
              <div className="min-w-0 flex-1 text-xs">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-foreground">{s.from_cat} {s.from_sel}</span>
                  <span className="text-muted-foreground">≡</span>
                  <span className="font-mono text-foreground">{s.to_cat} {s.to_sel}</span>
                  <span className="rounded bg-muted px-1.5 py-px text-[10px] text-muted-foreground">
                    {Math.round(s.similarity * 100)}% match{s.pair_count > 1 ? ` · ${s.pair_count} rooms` : ''}
                  </span>
                </div>
                <div className="mt-0.5 text-muted-foreground"><span className="text-muted-foreground">A:</span> {s.from_description}</div>
                <div className="text-muted-foreground"><span className="text-muted-foreground">B:</span> {s.to_description}</div>
              </div>
              <button
                type="button"
                onClick={() => onAcceptSuggestion(s.from_cat, s.from_sel, s.to_cat, s.to_sel)}
                className="mt-0.5 shrink-0 rounded-md bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700"
              >
                Confirm
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}

    <details className="rounded-md border">
      <summary className="cursor-pointer px-3 py-1.5 text-xs font-semibold text-muted-foreground">
        Pair manually {suggestions.length > 0 ? '(for anything not suggested)' : ''}
      </summary>
      <div className="space-y-3 p-3">
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-md border">
        <div className="border-b bg-rose-50 dark:bg-rose-500/15 px-3 py-1.5 text-xs font-semibold text-rose-800 dark:text-rose-300">
          Removed (estimate A only)
        </div>
        <div className="max-h-56 overflow-y-auto">
          {removedEntries.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">None.</p>
          )}
          {removedEntries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => onSelectRemoved(entry.key)}
              className={cn(
                'block w-full border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted',
                selectedRemoved === entry.key && 'bg-rose-100 dark:bg-rose-500/20',
              )}
            >
              <div className="font-mono text-foreground">{codeLabel(entry.baseline)}</div>
              <div>{entry.baseline?.description}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-md border">
        <div className="border-b bg-sky-50 dark:bg-sky-500/15 px-3 py-1.5 text-xs font-semibold text-sky-800 dark:text-sky-300">
          Added (estimate B only)
        </div>
        <div className="max-h-56 overflow-y-auto">
          {addedEntries.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">None.</p>
          )}
          {addedEntries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              onClick={() => onSelectAdded(entry.key)}
              className={cn(
                'block w-full border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted',
                selectedAdded === entry.key && 'bg-sky-100 dark:bg-sky-500/20',
              )}
            >
              <div className="font-mono text-foreground">{codeLabel(entry.comparison)}</div>
              <div>{entry.comparison?.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>

        <button
          type="button"
          onClick={onMarkEquivalent}
          disabled={!selectedRemoved || !selectedAdded}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <ArrowRightLeft className="h-4 w-4" /> Mark equivalent
        </button>
      </div>
    </details>

    {equivalences.length > 0 && (
      <div>
        <div className="mb-1.5 text-xs font-semibold text-muted-foreground">Confirmed equivalences</div>
        <ul className="space-y-1.5">
          {equivalences.map((eq, i) => (
            <li
              key={i}
              className="flex items-center justify-between rounded-md border bg-muted px-3 py-1.5 text-xs"
            >
              <span className="font-mono">
                {eq.from.cat} {eq.from.sel} ≡ {eq.to.cat} {eq.to.sel}
              </span>
              <button
                type="button"
                onClick={() => onRemoveEquivalence(i)}
                aria-label={`Remove equivalence ${i}`}
                className="text-muted-foreground hover:text-rose-700 dark:text-rose-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

export default ReconciliationWizard;
